import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import type { ProjectionConfig } from '../../../types/bi';
import styles from './QueryDesigner.module.css';

interface ProjectionConfigEditorProps {
  value: ProjectionConfig | null;
  onChange: (config: ProjectionConfig | null) => void;
}

export default function ProjectionConfigEditor({ value, onChange }: ProjectionConfigEditorProps) {
  const enabled = value !== null;

  const toggle = useCallback(() => {
    if (enabled) {
      onChange(null);
    } else {
      onChange({ type: 'linear', periods: 3 });
    }
  }, [enabled, onChange]);

  const update = useCallback(
    (patch: Partial<ProjectionConfig>) => {
      if (!value) return;
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  return (
    <div>
      <button className={styles.addBtn} onClick={toggle} style={{ marginBottom: '0.5rem' }}>
        <FontAwesomeIcon icon={enabled ? faToggleOn : faToggleOff} />
        {enabled ? ' Enabled' : ' Disabled'}
      </button>

      {enabled && value && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div className={styles.row}>
            <label style={{ fontSize: '0.8rem', minWidth: 60 }}>Type</label>
            <select
              className={styles.selectSm}
              value={value.type}
              onChange={(e) => update({ type: e.target.value as ProjectionConfig['type'] })}
            >
              <option value="linear">Linear regression</option>
              <option value="moving_avg">Moving average</option>
            </select>
          </div>

          <div className={styles.row}>
            <label style={{ fontSize: '0.8rem', minWidth: 60 }}>Periods</label>
            <input
              type="number"
              className={styles.input}
              value={value.periods}
              min={1}
              max={24}
              onChange={(e) =>
                update({ periods: Math.max(1, Math.min(24, Number(e.target.value) || 3)) })
              }
              style={{ width: 70 }}
            />
          </div>

          {value.type === 'moving_avg' && (
            <div className={styles.row}>
              <label style={{ fontSize: '0.8rem', minWidth: 60 }}>Window</label>
              <input
                type="number"
                className={styles.input}
                value={value.window ?? 3}
                min={2}
                max={12}
                onChange={(e) =>
                  update({ window: Math.max(2, Math.min(12, Number(e.target.value) || 3)) })
                }
                style={{ width: 70 }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
